/**
 * TUVI - Entregados y Liquidados
 * Módulo que agrupa los proyectos con estado 'entregado_liquidado'. Cuando en
 * la Lista de Proyectos se aplica ese estado, el proyecto se mueve aquí (y
 * desaparece de la lista). Desde aquí se puede revertir el estado para
 * devolverlo a la lista general.
 */
import React, { useState, useEffect, useCallback } from 'react';
import client from '../../services/graphqlClient';
import { useNotification } from '../../context/NotificationContext';
import './ListaProyectos.css';

const ProyectosLiquidados = ({ onVerDetalle }) => {
  const { error: showError, success: showSuccess } = useNotification();
  const [proyectos, setProyectos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');

  const cargarProyectos = useCallback(async () => {
    try {
      setLoading(true);
      const data = await client.query(`{
        proyectos(estado: "entregado_liquidado") {
          id fecha_cotizacion nombre_cliente cedula telefono correo
          proyecto_nombre apartamento area_m2
          valor_final estado estado_cartera monto_pagado porcentaje_pagado
        }
      }`);
      setProyectos(data.proyectos || []);
    } catch (err) {
      console.error('Error cargando proyectos liquidados:', err);
      showError('Error cargando proyectos liquidados');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { cargarProyectos(); }, [cargarProyectos]);

  // Revertir estado: el proyecto vuelve a la Lista de Proyectos
  const cambiarEstado = async (proyectoId, nuevoEstado) => {
    try {
      const data = await client.mutate(`
        mutation($id: Int!, $estado: String!) {
          actualizarEstadoProyecto(id: $id, estado: $estado) { success message }
        }
      `, { id: proyectoId, estado: nuevoEstado });
      if (data.actualizarEstadoProyecto.success) {
        setProyectos(prev => prev.filter(p => p.id !== proyectoId));
        showSuccess('Proyecto devuelto a la Lista de Proyectos');
      } else {
        showError(data.actualizarEstadoProyecto.message || 'Error al cambiar estado');
      }
    } catch (err) {
      showError('Error al cambiar estado: ' + err.message);
    }
  };

  const formatMoney = (valor) => new Intl.NumberFormat('es-CO', {
    style: 'currency', currency: 'COP', minimumFractionDigits: 0,
  }).format(valor || 0);

  const normalizar = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const filtrados = proyectos.filter(p => {
    const q = normalizar(busqueda.trim());
    if (!q) return true;
    return normalizar(p.proyecto_nombre).includes(q)
      || normalizar(p.nombre_cliente).includes(q)
      || normalizar(p.apartamento).includes(q);
  });

  const totalValor = filtrados.reduce((s, p) => s + (parseFloat(p.valor_final) || 0), 0);

  if (loading) {
    return (
      <div className="lista-proyectos-container">
        <div className="loading-state"><p>Cargando proyectos liquidados...</p></div>
      </div>
    );
  }

  return (
    <div className="lista-proyectos-container">
      {/* Header */}
      <div className="lista-header">
        <div className="header-info">
          <h1>Entregados y Liquidados</h1>
          <span className="contador">{filtrados.length} de {proyectos.length} proyectos</span>
        </div>
      </div>

      {/* Búsqueda */}
      <div className="filtros-section">
        <div className="busqueda-box">
          <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Buscar proyecto, cliente o apartamento..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Total */}
      <div className="estadisticas-cards">
        <div className="stat-card todos active">
          <div className="stat-header">
            <span className="stat-icon">✅</span>
            <span className="stat-title">Total Entregados y Liquidados</span>
          </div>
          <div className="stat-body">
            <span className="stat-cantidad">{filtrados.length}</span>
            <span className="stat-label">proyectos</span>
          </div>
          <div className="stat-footer">
            <span className="stat-valor">{formatMoney(totalValor)}</span>
          </div>
        </div>
      </div>

      {/* Tabla */}
      {filtrados.length === 0 ? (
        <div className="empty-state">
          <h3>No hay proyectos entregados y liquidados</h3>
          <p>{busqueda ? 'Intenta ajustar la búsqueda' : 'Cuando un proyecto pase a "Entregado y Liquidado" en la Lista de Proyectos, aparecerá aquí.'}</p>
        </div>
      ) : (
        <div className="tabla-container">
          <table className="proyectos-table">
            <thead>
              <tr>
                <th>Proyecto</th>
                <th>Cliente</th>
                <th>Apartamento</th>
                <th>Área (m²)</th>
                <th>Valor Final</th>
                <th>Pagado</th>
                <th>Saldo</th>
                <th>Devolver a Lista</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((proyecto) => {
                const saldo = (parseFloat(proyecto.valor_final) || 0) - (proyecto.monto_pagado || 0);
                return (
                  <tr
                    key={proyecto.id}
                    onClick={() => { if (onVerDetalle) onVerDetalle(proyecto.id); }}
                    className="proyecto-row"
                  >
                    <td>
                      <div className="proyecto-info">
                        <span className="proyecto-nombre">{proyecto.proyecto_nombre}</span>
                      </div>
                    </td>
                    <td>
                      <div className="cliente-info">
                        <span className="cliente-nombre">{proyecto.nombre_cliente}</span>
                        <span className="cliente-cedula">{proyecto.cedula}</span>
                      </div>
                    </td>
                    <td><span className="apartamento-badge">{proyecto.apartamento || '-'}</span></td>
                    <td><span className="area-value">{proyecto.area_m2 ? `${proyecto.area_m2} m²` : '-'}</span></td>
                    <td><span className="valor-final">{formatMoney(proyecto.valor_final)}</span></td>
                    <td>
                      {proyecto.monto_pagado > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <span className="valor-final" style={{ color: '#065f46', fontWeight: 600 }}>{formatMoney(proyecto.monto_pagado)}</span>
                          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{Number(proyecto.porcentaje_pagado || 0).toFixed(1)}%</span>
                        </div>
                      ) : (
                        <span style={{ color: '#cbd5e1' }}>—</span>
                      )}
                    </td>
                    <td>
                      <span style={{ color: saldo > 1000 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
                        {saldo > 1000 ? formatMoney(saldo) : '✓ Al día'}
                      </span>
                    </td>
                    <td>
                      <div className="acciones" onClick={(e) => e.stopPropagation()}>
                        <select
                          className="select-estado"
                          value="entregado_liquidado"
                          onChange={(e) => cambiarEstado(proyecto.id, e.target.value)}
                        >
                          <option value="entregado_liquidado">Entregado y Liquidado</option>
                          <option value="vendido">Vendido</option>
                          <option value="contratado_no_iniciado">Contratado</option>
                          <option value="en_ejecucion">En Ejecución</option>
                          <option value="entregado_con_detalles">Entregado con Detalles</option>
                          <option value="cobrado_liquidado">Entregado y en Cobro</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProyectosLiquidados;
